// Copyright © Orpyx® Medical Technologies Inc. 2025. All rights reserved.
// **ORPYX TRADE SECRET**
// **WARNING**: The contents of this repository, including source code and assets, are confidential. This source code and/or derived works are a trade secret of Orpyx Medical Technologies Inc. Unauthorized disclosure is strictly prohibited and may result in serious legal consequences.

import React, { useState, memo } from "react";
import {
    ModalWindowCancelButton,
    ModalWindowSubmitButton,
    useModalWindowStyle
} from "orpyx-web-common";
import {Dialog, DialogActions, DialogTitle, DialogContent, Grid, List, ListItem, ListItemText, ListItemIcon, TextField} from "@material-ui/core";
import { useDocumentUploadStyles } from "./document-upload.style";
import Alert from "@material-ui/lab/Alert";
import ClearIcon from '@material-ui/icons/Clear';
import { nameLengthValidator } from "modules/shared/utility/file-extension";
import WarningIcon from '@material-ui/icons/Warning';
import { DocumentUploadEvents, DocumentUploadMeta } from "./document-upload.interface";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { CANCEL_DOCUMENT_UPLOAD, PREPARE_FOR_UPLOAD } from "modules/doctor-dashboard/store/document-list/document-list.actions";
import Dropzone, {DropEvent, FileRejection} from 'react-dropzone';
import { DocumentUploadListItem } from "./document-upload-list-item";
import { set } from "js-cookie";

const DocumentUploadTemplate = memo((props: DocumentUploadMeta) => {
    const MAX_FILE_SIZE = 26214400; // 25 MB falls below the backend limit of 30MB. Increasing this value above 30MB will require an adjustment to MaxRequestBodySize in orpyx-cloud-webapis/Cloud.Documents.Api
    const MAX_NUMBER_OF_DOCUMENTS_AT_ONCE = 10;
    const FILE_TOO_LARGE_ERROR = 'file-too-large'
    const FILE_INVALID_TYPE_ERROR = 'file-invalid-type';
    const classes = useModalWindowStyle();
    const docUploadClasses = useDocumentUploadStyles();

    const [validationError, setValidationError] = useState<null | string>(null);
    const [description, setDescription] = useState("");
    const [myFiles, setMyFiles] = React.useState<File[]>([]);
    const [myRejectedFiles, setMyRejectedFiles] = React.useState<FileRejection[]>([]);

    const onDrop = React.useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
            const newAccepted = acceptedFiles.filter((file) => {
                return !myFiles.some(f => f.name === file.name);
            });
            const accepted = [...myFiles, ...newAccepted];


            const newRejected = fileRejections.filter((rejection) => {
                return !myRejectedFiles.some(r => r.file.name === rejection.file.name);
            });
            const rejected = [...myRejectedFiles, ...newRejected];
            
            validateFiles(accepted, rejected, description);

            setMyFiles(accepted);
            setMyRejectedFiles(rejected);
        },
        [myFiles, myRejectedFiles]
    );

    const handleSubmit = () => {
        props.onPrepareDocumentUpload(myFiles, description);
        resetLocalState();
    };

    const handleClose = () => {
        props.onCancelDocumentUpload();
        resetLocalState();
    };

    const resetLocalState = () => {
        setDescription('');
        setValidationError(null);
        setMyFiles([]);
        setMyRejectedFiles([]);
    }

    const handleDescriptionChange = (x: any) => {
        validateFiles(myFiles, myRejectedFiles, x?.target?.value);
        setDescription(x.target.value);
    }

    const removeFile = (filename: string) => {
        const update = myFiles.filter(x => x.name !== filename);
        validateFiles(update, myRejectedFiles, description);

        setMyFiles(update);
    };

    const removeRejectedFile = (filename: string) => {
        const update = myRejectedFiles.filter(x => x.file.name !== filename);
        validateFiles(myFiles, update, description);

        setMyRejectedFiles(update);
    };
    
    const validateFiles = (accepted: File[], rejected: FileRejection[], description: string) => {
        setRejectedFilesErrors(rejected);
        const error = getRejectedFilesValidationError(rejected);
        if(error !== null) {
            setValidationError(error);
            return;
        }

        if(accepted.length > MAX_NUMBER_OF_DOCUMENTS_AT_ONCE) {
            setValidationError("Too many files. You can upload a maximum of " + MAX_NUMBER_OF_DOCUMENTS_AT_ONCE + " documents at once.");
            return;
        }

        setValidationError(getFilesDescriptionError(description));
    };

    const setRejectedFilesErrors = (rejected: FileRejection[]) => {
        for (const file of rejected) {
            for (const error of file.errors) {                
                if (error.code == FILE_TOO_LARGE_ERROR) {
                    error.message = `File is larger than ${MAX_FILE_SIZE / 1024 / 1024} MB`
                }
                if(error.code == FILE_INVALID_TYPE_ERROR) {
                    error.message = 'File type not accepted. Only pdf, jpg, and png files are allowed.'
                }
            }
        }
    };

    const getFilesDescriptionError = (description: string): string | null => {
        if(description.length > 500) {
            return "Description cannot be more than 500 characters in length.";
        }
        return null;
    };

    const getRejectedFilesValidationError = (rejected: FileRejection[]): string | null => {
        for (const file of rejected) {
            if(file.errors.some(e => e.code === FILE_INVALID_TYPE_ERROR)) {
                return "Only pdf, jpg, and png files are allowed.";
            }
            if(file.errors.some(e => e.code === FILE_TOO_LARGE_ERROR)) {
                return "No individual file may exceed " + MAX_FILE_SIZE/1024/1024 + " MB";
            }
        }
        return null;
    };

    return (<Dialog
                fullWidth={true}
                open={props.open}
                classes = {{paper: classes.dialog}}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                    <DialogTitle id="alert-dialog-title">Upload documents (pdf, jpg, png) for {props.name}</DialogTitle>
                    <DialogContent>
                        <div className={docUploadClasses.margin} id="document-uploads-grid">
                            {validationError && <Alert variant="outlined" severity="error" className={docUploadClasses.margin}>{validationError}</Alert>}
                            <Grid container spacing={1} alignItems="flex-end">
                                <Grid item xs={12}>
                                    <Dropzone onDrop={onDrop} maxSize={MAX_FILE_SIZE}
                                        validator={nameLengthValidator} accept={{ 'application/pdf': [".pdf"], 'image/jpeg': [".jpeg", ".jpg"], 'image/png': [".png"] }}>
                                        {({getRootProps, getInputProps, isDragActive}) => (
                                        <div {...getRootProps({className: docUploadClasses.baseStyle })}>
                                            <input {...getInputProps()} />
                                            {isDragActive ? (
                                                <p>                        Drop the files here ...                         </p>
                                            ) : (
                                                <p>Drag 'n' drop medical reference documents here, or click to select files</p>
                                            )}
                                        </div>)}
                                    </Dropzone>
                                </Grid>
                                {myFiles && myFiles.length !== 0 &&  
                                <Grid item xs={12}>
                                    <div>
                                        <List dense={false}>
                                            {myFiles.map((file, index) => (
                                                <DocumentUploadListItem key={index} file={file}>
                                                    <ListItemIcon className={docUploadClasses.clickable} onClick={() => removeFile(file.name)}>
                                                        <ClearIcon fontSize="small" />
                                                    </ListItemIcon>
                                                </DocumentUploadListItem>
                                            ))}
                                        </List>
                                    </div>
                                </Grid>}
                                {myRejectedFiles && myRejectedFiles.length !== 0 && <Grid item xs={12}>
                                    <div>
                                        <List dense={false}>
                                            {myRejectedFiles.map((rejection: FileRejection, index: number) => (
                                                <ListItem key={index}>
                                                    <ListItemIcon>
                                                        <WarningIcon color="error" fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={rejection.file.name} />
                                                    <ul>
                                                        {rejection.errors.map(e => <li key={e.code}>{e.message}</li>)}
                                                    </ul>
                                                    <ListItemIcon className={docUploadClasses.clickable} onClick={() => removeRejectedFile(rejection.file.name)}>
                                                        <ClearIcon fontSize="small" />
                                                    </ListItemIcon>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </div>
                                </Grid>}
                                <Grid item xs={12}>
                                    {myFiles && myFiles.length !== 0 && <TextField
                                        fullWidth
                                        multiline={true}
                                        id="description"
                                        name="description"
                                        label="Description"
                                        onChange={handleDescriptionChange}
                                        />}
                                </Grid>
                            </Grid>
                        </div>
                    </DialogContent>
                <DialogActions style={{ justifyContent: "center" }}>
                    <ModalWindowSubmitButton onSubmit={handleSubmit}
                        content={"Upload"} disabled={!(myFiles && myFiles.length !== 0) || (validationError !== null)} />
                    <ModalWindowCancelButton onCancel={handleClose}
                        content={"Close"} disabled={false} />
                </DialogActions>
            </Dialog>)
});


const mapDispatchToProps = (dispatch: Dispatch): DocumentUploadEvents => {
    return bindActionCreators({
        onCancelDocumentUpload: CANCEL_DOCUMENT_UPLOAD,
        onPrepareDocumentUpload: PREPARE_FOR_UPLOAD
    }, dispatch);
};

export const DocumentUpload = memo(connect(null,mapDispatchToProps)(DocumentUploadTemplate));